/**
 * React хук для работы с backend API
 * Предоставляет удобный интерфейс для загрузки и сохранения данных
 */

import { useState, useCallback } from 'react';
import {
  getDiagrams,
  getDiagramById,
  createDiagram,
  updateDiagram,
  deleteDiagram,
  listSystems,
  getSystemById,
  getSystemByCode,
  isBackendEnabled,
} from '../api';

/**
 * Хук для работы с диаграммами
 */
export const useDiagrams = () => {
  const [diagrams, setDiagrams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDiagrams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDiagrams();
      setDiagrams(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Ошибка загрузки диаграмм:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDiagram = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDiagramById(id);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Ошибка загрузки диаграммы:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveDiagram = useCallback(async (diagramData, id = null) => {
    setLoading(true);
    setError(null);
    try {
      let result;
      if (id) {
        result = await updateDiagram(id, diagramData);
      } else {
        result = await createDiagram(diagramData);
      }
      // Обновляем список после сохранения
      await loadDiagrams();
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Ошибка сохранения диаграммы:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadDiagrams]);

  const removeDiagram = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await deleteDiagram(id);
      // Обновляем список после удаления
      await loadDiagrams();
    } catch (err) {
      setError(err.message);
      console.error('Ошибка удаления диаграммы:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadDiagrams]);

  return {
    diagrams,
    loading,
    error,
    loadDiagrams,
    loadDiagram,
    saveDiagram,
    removeDiagram,
  };
};

/**
 * Хук для работы с системами
 */
export const useSystems = () => {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSystems = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSystems(params);
      setSystems(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Ошибка загрузки систем:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSystem = useCallback(async (idOrCode, byCode = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = byCode
        ? await getSystemByCode(idOrCode)
        : await getSystemById(idOrCode);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Ошибка загрузки системы:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    systems,
    loading,
    error,
    loadSystems,
    loadSystem,
  };
};

/**
 * Хук для проверки доступности backend
 */
export const useBackendStatus = () => {
  const isConnected = true;
  const checking = false;

  return {
    isConnected,
    checking,
    usingBackend: isBackendEnabled(),
  };
};

/**
 * Универсальный хук для любых API запросов
 */
export const useApiRequest = (apiFunction) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFunction(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Ошибка API запроса:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset,
  };
};
